// Backend stores money as integer minor units. Convert on display only.

import { getLocaleTag, t } from './i18n.js';
import { esc } from './component.js';

export function formatMoney(minor, currency = 'EUR', locale = undefined) {
  if (minor === null || minor === undefined) return '—';
  const value = Number(minor) / 100;
  const tag = locale || getLocaleTag();
  // HU + HUF never carries fillér decimals in everyday use — drop them
  // so 150000.00 Ft becomes "150 000 Ft". Other currencies still get
  // their normal two-decimal display.
  const opts = { style: 'currency', currency };
  if (currency === 'HUF' || Number.isInteger(value)) {
    opts.minimumFractionDigits = 0;
    opts.maximumFractionDigits = 0;
  }
  try {
    return new Intl.NumberFormat(tag, opts).format(value);
  } catch (_) {
    return `${value.toFixed(2)} ${currency}`;
  }
}

/**
 * Plain thousands-separated integer in the active locale (hu-HU uses
 * a non-breaking space, en uses commas). Use for raw numeric labels
 * like rent / közös költség values that aren't currency-formatted.
 */
export function formatInt(value, locale = undefined) {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  const tag = locale || getLocaleTag();
  try {
    return new Intl.NumberFormat(tag, { maximumFractionDigits: 0 }).format(Math.round(n));
  } catch (_) {
    return String(Math.round(n));
  }
}

/**
 * Strip trailing fractional zeros for sizes ("58.00" → "58", "58.50" → "58,5").
 * Uses the locale's decimal separator.
 */
export function formatSize(value, locale = undefined) {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  const tag = locale || getLocaleTag();
  try {
    return new Intl.NumberFormat(tag, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    }).format(n);
  } catch (_) {
    return String(Math.round(n * 100) / 100);
  }
}

export function formatDate(value, locale = undefined) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(locale || getLocaleTag(), { dateStyle: 'medium' }).format(d);
}

export function formatDateTime(value, locale = undefined) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(locale || getLocaleTag(), { dateStyle: 'medium', timeStyle: 'short' }).format(d);
}

// Escape user-provided multi-line text for safe HTML interpolation while
// preserving line breaks (newlines become <br>). Useful for free-text fields
// such as property descriptions where the input contains emoji-prefixed lines.
export function renderMultiline(text) {
  if (text === null || text === undefined) return '';
  return esc(String(text)).replace(/\r\n|\r|\n/g, '<br>');
}

/**
 * Derive the property's display address from its cached row.
 *
 * Backend caches the main unit's address on the property row, so the
 * common single-location case formats fine from `address_line1 / city /
 * postal_code`. When the published / managed units span more than one
 * distinct street tuple (`unit_location_count > 1`), the address cache
 * would misleadingly point at just one of them — fall back to a
 * "City, N helyszín" summary so it's clear the bundle is multi-site.
 *
 * Returns a plain string; the caller is responsible for `esc()` when
 * interpolating into a template.
 */
export function displayAddress(p) {
  if (!p) return '';
  const count = Number(p.unit_location_count || 0);
  const city = (p.city || '').toString().trim();
  if (count > 1 && city !== '') {
    return t('properties.display_address.multi', { city, count });
  }
  const cityLine = [p.postal_code, p.city].filter(Boolean).join(' ');
  return [p.address_line1, p.address_line2, cityLine, p.country]
    .map((s) => (s === null || s === undefined ? '' : String(s).trim()))
    .filter((s) => s !== '')
    .join(', ');
}
